import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const RefreshControl = ({ onRefresh, autoRefreshInterval, onIntervalChange }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(autoRefreshInterval);

  const intervals = [
    { value: 60000, label: '1 min' },
    { value: 300000, label: '5 min' },
    { value: 900000, label: '15 min' },
    { value: 0, label: 'Off' }
  ];

  useEffect(() => {
    if (autoRefreshInterval === 0) return;

    const timer = setInterval(() => {
      setTimeUntilRefresh(prev => {
        if (prev <= 1000) {
          handleRefresh();
          return autoRefreshInterval;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [autoRefreshInterval]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setLastRefresh(new Date());
    setTimeUntilRefresh(autoRefreshInterval);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatCountdown = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-card border border-border rounded-lg p-3 md:p-4">
      <button
        onClick={handleRefresh}
        disabled={isRefreshing}
        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-smooth disabled:opacity-50"
      >
        <Icon 
          name="RefreshCw" 
          size={16} 
          className={isRefreshing ? 'animate-spin' : ''}
        />
        <span className="text-sm font-medium">Refresh</span>
      </button>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Auto-refresh:</span>
        {intervals?.map((interval) => (
          <button
            key={interval?.value}
            onClick={() => {
              onIntervalChange(interval?.value);
              setTimeUntilRefresh(interval?.value);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-smooth ${
              autoRefreshInterval === interval?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            {interval?.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <Icon name="Clock" size={14} className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          Last updated: {formatTime(lastRefresh)}
        </span>
        {autoRefreshInterval > 0 && (
          <span className="text-xs text-muted-foreground">
            (Next: {formatCountdown(timeUntilRefresh)})
          </span>
        )}
      </div>
    </div>
  );
};

export default RefreshControl;